import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  ProductReviewControllerApiFactory,
  type ProductReviewServiceSummaryResponseV1,
} from '@redteclab/api/clients/bully'

import { useGlooApi } from '../../../../api'
import { useGlobalConfigContext } from '../../../../global-config'
import {
  type ProductReviewsCustomerReview,
  type ProductReviewsRatingSummaryValues,
  ProductReviewsSummaryAndReviewsList,
} from '../../../../product'
import { useProductPageContext } from '../../productPageContext'

import { ProductReviewsLanguageSwitcher } from './ProductReviewLanguageSwitcher/ProductReviewsLanguageSwitcher'
import {
  convertProductReviewToComponentReviewType,
  useProductReviewsControls,
} from './useProductReviewsControls'

const convertSummaryToComponentSummaryType = (
  summary: ProductReviewServiceSummaryResponseV1,
): ProductReviewsRatingSummaryValues => {
  return {
    averageRating: summary.averageRating,
    numberOfFiveStarReviews: summary.starsFive,
    numberOfFourStarReviews: summary.starsFour,
    numberOfOneStarReviews: summary.starsOne,
    numberOfThreeStarReviews: summary.starsThree,
    numberOfTwoStarReviews: summary.starsTwo,
  }
}

const ProductReviews: FC = () => {
  const { channel, product, reviewData } = useProductPageContext()
  const globalConfig = useGlobalConfigContext()
  const client = useGlooApi(ProductReviewControllerApiFactory)

  const reviewsControls = useProductReviewsControls({
    reviewData,
    reviewsPreloadedOnServer: reviewData.productReviews.reviews,
  })

  const reviewsFetcher = async (
    page: number,
    pageSize: number,
  ): Promise<ProductReviewsCustomerReview[]> => {
    if (!product.variant_code) {
      return []
    }

    const response =
      await client.productReviewV1LanguageTenantVariantsVariantCodeReviewsGet(
        {
          language: reviewsControls.currentReviewsLanguage,
          page,
          pageSize,
          tenant: globalConfig.tenantAndEnv,
          variant_code: product.variant_code,
        },
        {
          params: {
            channel,
          },
        },
      )

    return response.data.reviews.map((review) =>
      convertProductReviewToComponentReviewType(review),
    )
  }

  const totalRatings = reviewsControls.showLanguageSwitcher
    ? reviewsControls.reviewsSummary.ratingCount
    : reviewsControls.reviewsCount

  return (
    <div data-qa-id="product-ratings-container">
      <h2 className="mb-5 break-words text-2xl font-medium">
        {totalRatings ? (
          <FormattedMessage
            id="general.label.product.numberOfRatingsFor"
            values={{
              numberOfRatings: totalRatings,
              productName: product.name,
              productReviewSuperScript: null,
            }}
          />
        ) : (
          product.name
        )}
      </h2>
      <ProductReviewsSummaryAndReviewsList
        id={`product/${product.upid}/reviews`}
        initialReviews={reviewsControls.prefetchedDataForLanguage}
        languageSwitcherSlot={
          reviewsControls.showLanguageSwitcher ? (
            <ProductReviewsLanguageSwitcher
              currentReviewsLanguage={reviewsControls.currentReviewsLanguage}
              selectableLanguages={reviewsControls.selectableLanguageData}
              setLanguage={reviewsControls.setCurrentReviewsLanguage}
            />
          ) : null
        }
        onFetchReviews={reviewsFetcher}
        productUpidForReview={product.upid}
        reviewsLanguage={reviewsControls.currentReviewsLanguage}
        summary={convertSummaryToComponentSummaryType(
          reviewsControls.reviewsSummary,
        )}
        totalReviews={reviewsControls.reviewsCount}
      />
    </div>
  )
}

export { ProductReviews }
